/**
 * Where a piece of work stands on its way into the repository, and the words
 * for it — one definition for every place that says so: the Timeline's commit
 * markers and legend, the Git view's branches and merge queue, the agent
 * cards (roadmap D180). The stage itself is worked out by the host
 * (src/git/commitTimeline.ts); its type is shared/git/workStage.ts.
 *
 * Four stages, furthest along last:
 *
 *   working  — "Being worked on": only on an agent's own branch
 *              (`mojoup/<team>/<member>…`) or another branch nobody has merged.
 *   landing  — "Approved — going in": on an integration branch
 *              (`mojoup/integration/<target>`), not yet in its target. The
 *              workbench is merging it in. (The id stays `landing`: code.)
 *   plan     — "In PLAN-2's branch": in the plan's own branch (`feat/<slug>`),
 *              not yet in main. Merged for the plan; merging into main is the
 *              person's step.
 *   main     — "In main": reachable from the repository's default branch.
 *
 * Plain words for people, the same everywhere: they come from
 * shared/wording/merging.ts, which the host's notices use too. Pure and
 * DOM-free; its tests load it through `createRequire`, as shared/ is CommonJS
 * (test/timelineCommitsModel.test.ts).
 */
import type { RemoteStanding, WorkStage } from '@aioe/shared/git/workStage';
import { APPROVED_GOING_IN, BEING_WORKED_ON, IN_MAIN, IN_PLAN_BRANCH, mergedInto } from '@aioe/shared/wording/merging';

export type { RemoteStanding, WorkStage };

/** Least to furthest along: the order a legend lists them in. */
export const WORK_STAGES: readonly WorkStage[] = ["working", "landing", "plan", "main"];

/** How far along a stage is (0 = being worked on, 3 = in main). */
export function stageRank(stage: WorkStage): number {
  return WORK_STAGES.indexOf(stage);
}

export interface StageWordsContext {
  /** The plan, as people say it: its reference (PLAN-2), else a short name. */
  plan?: string;
  /** The repository's default branch; "main" when unknown. */
  main?: string;
}

/** The stage in words: "Being worked on", "Approved — going in", "In PLAN-2's branch", "In main". */
export function workStageLabel(stage: WorkStage, ctx: StageWordsContext = {}): string {
  switch (stage) {
    case "working":
      return BEING_WORKED_ON;
    case "landing":
      return APPROVED_GOING_IN;
    case "plan":
      return IN_PLAN_BRANCH(ctx.plan);
    case "main":
      return IN_MAIN(ctx.main || "main");
  }
}

/** A legend's words, where no one plan is meant: "In a plan's branch". */
export function workStageLegendLabel(stage: WorkStage, main?: string): string {
  return stage === "plan" ? "In a plan's branch" : workStageLabel(stage, { main });
}

/** One sentence that says what the stage means, for a hover. */
export function workStageHint(stage: WorkStage, ctx: StageWordsContext = {}): string {
  const main = ctx.main || "main";
  switch (stage) {
    case "working":
      return "Only on the agent's own branch: not approved or merged anywhere yet.";
    case "landing":
      return "Approved, and on its way in: the workbench merges it in by itself.";
    case "plan":
      return `${mergedInto(ctx.plan ? `${ctx.plan}'s branch` : "the plan's branch")}, waiting for you to merge it into ${main}.`;
    case "main":
      return `${mergedInto(main)}.`;
  }
}

/** "Not on GitHub yet", "Only on GitHub". */
export function remoteStandingLabel(standing: RemoteStanding, remote?: string): string {
  const where = remote || "the remote";
  return standing === "not-pushed" ? `Not on ${where} yet` : `Only on ${where}`;
}

/**
 * A branch's stage from what the Git model already knows of it: merged into
 * the base, into the integration branch, or neither. Undefined for a branch
 * with nothing of its own (no commits ahead, merged nowhere).
 */
export function branchStage(b: { mergedIntoBase: boolean; mergedIntoIntegration: boolean; ahead?: number }): WorkStage | undefined {
  if (b.mergedIntoBase) return "main";
  if (b.mergedIntoIntegration) return "landing";
  return (b.ahead ?? 0) > 0 ? "working" : undefined;
}
